import React from "react";
import {
  Building2,
  FileText,
  Calendar,
  Users,
  Shield,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskList from "../../assets/new-images/ca1.png";
import taskDetail from "../../assets/new-images/ca2.png";
import calendarView from "../../assets/ca/ca-calendar.png";
import capsuleView from "../../assets/ca/ca-capsule.png";
import dashboard from "../../assets/new-images/Dashboard.png";
import performance from "../../assets/ca/ca-perfomance.png";
import events from "../../assets/ca/ca-events.png";
import reminders from "../../assets/ca/ca-reminders.png";
import attendance from "../../assets/new-images/collabd.png";
import manageRequests from "../../assets/new-images/it4.png";
import manageTimesheets from "../../assets/new-images/it5.png";
import clientDocuments from "../../assets/ca/ca-clients.png";
import clientLogin from "../../assets/ca/clientlogin-cafirm.png";

const features = [
  {
    icon: <FileText size={26} />,
    title: "Client Document Management",
    description:
      "Centralize all client files, tax documents, and compliance records in one secure location with version control.",
  },
  {
    icon: <Calendar size={26} />,
    title: "Deadline Tracking",
    description:
      "Never miss a filing deadline again with automated reminders and visual deadline calendars.",
  },
  {
    icon: <Users size={26} />,
    title: "Client Portal",
    description:
      "Give clients secure access to their documents and status updates without endless email chains.",
  },
  {
    icon: <Shield size={26} />,
    title: "Compliance Workflows",
    description:
      "Build repeatable workflows for tax seasons, audits, and regulatory filings.",
  },
];

const benefits = [
  "Reduce client email volume by 60%",
  "Track every client engagement in one place",
  "Automate tax season reminders and follow-ups",
  "Secure document sharing with audit trails",
  "Multi-step approval workflows for complex filings",
];

const showcase = [
  {
    eyebrow: "Practice-wide task board",
    title: "GPCB licenses, GST, audits, and routine work — one board",
    description:
      "See every task across GPCB licenses, GST Registration, Income Tax Audits, CGST Notices, and SGST Appeals, with client, status, period, and financial year in one grid — filtered by GST, Income Tax, or Calendar view whenever you need to narrow in.",
    image: taskList,
    alt: "Simplifyte task list showing GPCB licenses, Income Tax Audit, GST Registration, CGST Notice, and SGST Appeals tasks with client names and status",
  },
  {
    eyebrow: "Task-level detail",
    title: "Every task, with the client and deadline context that matters",
    description:
      "Open any task to see its client, financial year, period, due date, and assignees at a glance — so a routine job like 'Accounting @Office' carries exactly the context a partner or associate needs, without a separate lookup.",
    image: taskDetail,
    alt: "Simplifyte task detail panel showing an Accounting @Office task with client, financial year, period, due date, and assignees",
  },
  {
    eyebrow: "Calendar View",
    title: "See overdue and upcoming work laid out by day",
    description:
      "Every task due across every client — AKG Alcotec, Alnoor Enterprise, Metal India Enterprise, and more — plotted on a month calendar, so a partner can spot a pile-up on a specific day before it becomes a scramble.",
    image: calendarView,
    alt: "Simplifyte calendar view showing tasks due across different days in July for clients like AKG Alcotec, Alnoor Enterprise, and Metal India Enterprise",
  },
  {
    eyebrow: "Capsule View",
    title: "Every task's full context, in one scannable row",
    description:
      "Client, due date, assignees, and remarks for every task — Stock Statements, Net Worth Certificates, GSTR filings, Audited Balance Sheets — laid out side by side, so a reviewer doesn't have to open each task to know what's actually going on.",
    image: capsuleView,
    alt: "Simplifyte capsule view showing tasks like Stock Statement, Net Worth Certificate, and GSTR-10 with client, due date, assignee, and remarks columns",
  },
  {
    eyebrow: "Daily performance dashboard",
    title: "Know where your firm stands, every morning",
    description:
      "A single dashboard shows today's task movement, overdue items, closures, and upcoming due dates and reminders — so partners can catch a slipping deadline before it becomes a client escalation.",
    image: dashboard,
    alt: "Simplifyte dashboard showing today's tasks, performance percentage, pending and closed task counts, and upcoming reminders",
  },
  {
    eyebrow: "Value & Performance Analysis",
    title: "Turn logged work into a delivery report partners can act on",
    description:
      "Delivery rate, tasks completed, hours logged, and adjusted cost roll up automatically per team member — with On Track and Monitor flags that surface who needs support before review season, not after.",
    image: performance,
    alt: "Simplifyte Value Delivery Report showing delivery rate, tasks completed, hours logged, adjusted cost, and per-user performance status",
  },
  {
    eyebrow: "Events",
    title: "Client meetings and filing reviews, never buried in a calendar invite",
    description:
      "Monthly Client Accounts Reviews, Income Tax Return Filing Reviews, Annual Audit Planning Meetings, and GST Compliance Review Meetings — every client-facing event in one place alongside your task deadlines.",
    image: events,
    alt: "Simplifyte Events screen showing client meetings including Monthly Client Accounts Review, Income Tax Return Filing Review, and Annual Audit Planning Meeting",
  },
  {
    eyebrow: "Reminders",
    title: "Follow-ups that don't depend on someone remembering",
    description:
      "Verify TDS Return Details, Review Pending Income Tax Notices — pending, overdue, and completed reminders tracked against the calendar, so nothing slips just because it wasn't urgent yet.",
    image: reminders,
    alt: "Simplifyte Reminders screen showing pending reminders including Verify TDS Return Details and Review Pending Income Tax Notices",
  },
  {
    eyebrow: "Staff attendance & timesheets",
    title: "Attendance and billable hours, sorted",
    description:
      "Track present days, late arrivals, overtime, and leave for every staff member against a monthly calendar — with attendance rate and average work hours rolled up automatically for payroll and client billing.",
    image: attendance,
    alt: "Simplifyte monthly attendance report for a CA firm staff member showing present days, overtime, leave days, and a calendar view",
  },
  {
    eyebrow: "Team & Workforce Management",
    title: "Leave, attendance, and presence requests, approved in one place",
    description:
      "Leave requests, present requests, and approvals for articled assistants, associates, and partners flow through one queue with requester, remarks, and status — so nobody's chasing sign-offs over WhatsApp during audit season.",
    image: manageRequests,
    alt: "Simplifyte Manage Requests showing a table of leave and present requests with requester, remarks, status, and action-by columns",
  },
  {
    eyebrow: "Attendance & Timesheets",
    title: "Check-ins, overtime, and absences, at a glance",
    description:
      "See present records, late arrivals, overtime, and absences for the entire firm in one timesheet view, with check-in times and status per person — so attendance issues surface immediately instead of at month-end, right alongside billable hours.",
    image: manageTimesheets,
    alt: "Simplifyte Manage Timesheets showing team check-in, check-out, work time, overtime, and break time for each employee with status labels",
  },
  {
    eyebrow: "Client Documents",
    title: "Every client's files, organized in their own folder",
    description:
      "Horizon Logistics & Transport, BlueStar Electronics, Greenfield Developers, Sunrise Healthcare, Mehta & Associates — each client gets a dedicated folder with upload tracking, a client-visibility toggle, and a clear record of who shared what and when.",
    image: clientDocuments,
    alt: "Simplifyte Client Documents screen showing folders for Horizon Logistics and Transport, BlueStar Electronics, Greenfield Developers, and Sunrise Healthcare with upload and share details",
  },
  {
    eyebrow: "Client Login Portal",
    title: "Your clients see their own status — without calling your office",
    description:
      "Every client gets their own login to a dashboard showing their pending and completed tasks, upcoming filing deadlines, and document status — so a client like Aafiya Green Pvt Ltd can check where their GST filing stands without a phone call to your team.",
    image: clientLogin,
    alt: "Simplifyte client login dashboard for Aafiya Green Pvt Ltd, a trading firm client, showing pending tasks, upcoming events, and completed tasks by month",
  },
];

const CAFirmsPage = () => {
  return (
    <>
      <HeaderMenu />
      <div className="CA_class_main">
        {/* Hero */}
        <section className="CA_class_hero">
          <div className="CA_class_container">
            <div className="CA_class_hero_content">
              <div className="CA_class_icon_box">
                <Building2 size={34} />
              </div>

              <h1 className="CA_class_heading">
                Work Clarity Software built for CA Firms
              </h1>

              <p className="CA_class_subheading">
                Manage client documents, deadlines, and multi-step compliance
                workflows without losing sleep over missed emails.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="CA_class_features_section">
          <div className="CA_class_container">
            <h2 className="CA_class_section_title">
              Everything you need for modern professional practice
            </h2>

            <div className="CA_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="CA_class_feature_card">
                  <div className="CA_class_feature_icon">{feature.icon}</div>

                  <h3 className="CA_class_feature_title">{feature.title}</h3>

                  <p className="CA_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="CA_class_showcase_section">
          <div className="CA_class_container">
            <h2 className="CA_class_section_title CA_class_center">
              See how CA firms run on Simplifyte
            </h2>
            <p className="CA_class_showcase_subtitle">
              Real screens from the product — built around how compliance
              teams actually track notices, filings, and client work.
            </p>

            <div className="CA_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`CA_class_showcase_row ${
                    index % 2 === 1 ? "CA_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="CA_class_showcase_text">
                    <span className="CA_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="CA_class_showcase_title">{item.title}</h3>
                    <p className="CA_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="CA_class_showcase_media">
                    <div className="CA_class_showcase_frame_outer">
                      <div className="CA_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="CA_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="CA_class_benefits_section">
          <div className="CA_class_container">
            <div className="CA_class_benefits_wrapper">
              <h2 className="CA_class_section_title CA_class_center">
                Why CA firms choose Simplifyte
              </h2>

              <div className="CA_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="CA_class_benefit_item">
                    <CheckCircle2 size={24} className="CA_class_check_icon" />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="CA_class_cta_section">
          <div className="CA_class_container">
            <div className="CA_class_cta_box">
              <h2 className="CA_class_cta_title">
                Ready to simplify your practice?
              </h2>

              <p className="CA_class_cta_text">
                Join CA firms who've reduced client communication chaos and
                never miss a deadline.
              </p>

              <button
                className="CA_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default CAFirmsPage;
